import { Button } from '@ui-components/neo.tax';
import { Link } from 'react-router-dom';

import {
  AnalysisType,
  BackgroundOperationName,
  EMAIL_TO_SUPPORT_URL,
  PageLayoutProps,
  Path,
  getPath,
  useBackgroundOperations,
  useCurrentCompany,
  useCurrentCompanyId,
  useUnfinishedSyncEventInPeriodByAnalysisType,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { getMonthLockExceptionsText } from '../utils/asc-period-lock.utils';
import { useAscPeriodLockStatus } from './useAscPeriodLockStatus';
import { useCanSetAscPeriodLock } from './useCanSetAscPeriodLock';

type PageLockStatus = {
  loading: boolean;
  pageLockStatus: Pick<PageLayoutProps, 'locked' | 'lockButtons' | 'lockedReason'>;
};

export const useAscPageLockStatus = ({
  disableIfAscCostPendingValidation,
  disableIfOngoingSync,
  disableIfPeriodLocked,
}: {
  disableIfOngoingSync?: boolean;
  disableIfAscCostPendingValidation?: boolean;
  disableIfPeriodLocked?: boolean;
} = {}): PageLockStatus => {
  const { fiscalYearId, periodName } = useAscContext();
  const companyId = useCurrentCompanyId();
  const { loading: loadingUnfinishedSyncEventsPeriod, period: unfinishedSyncEventsPeriod } =
    useUnfinishedSyncEventInPeriodByAnalysisType({ fiscalYearId, periodName, analysisType: AnalysisType.Asc });
  const { company } = useCurrentCompany();
  // Every branch below reads the lock status only under `disableIfPeriodLocked`, so fetching it for
  // the other callers costs a request whose result is thrown away.
  const {
    error: periodLockStatusError,
    loading: loadingPeriodLockStatus,
    lockStatus,
  } = useAscPeriodLockStatus({ fiscalYearId, periodName, skip: !disableIfPeriodLocked });
  const canSetLock = useCanSetAscPeriodLock();
  const backgroundOperationsResult = useBackgroundOperations();
  const hasInFlightBulkUpload =
    !backgroundOperationsResult.loading &&
    backgroundOperationsResult.backgroundOperations.some(
      (op) => op.name === BackgroundOperationName.ImportAscProjectsFileOperation && op.progressPercent < 100,
    );

  // A status still in flight is a status we do not have, which puts this branch in the same position
  // as the error branch below: the lock is enforced here and nowhere else, so an unknown status has
  // to close the page rather than open it. Callers that opted in hold the page back on `loading`
  // instead of showing this, so a closed page is only what they fall back to if they forget.
  if (loadingUnfinishedSyncEventsPeriod || (disableIfPeriodLocked && loadingPeriodLockStatus)) {
    return {
      loading: true,
      pageLockStatus: {
        locked: Boolean(disableIfPeriodLocked),
      },
    };
  }

  // The lock is enforced here and nowhere else, so an unreadable status has to close the page rather
  // than open it: falling through would hand back a fully editable page for a period an Admin has
  // closed, and the writes would land. `getAscPeriodLockStatus` is non-nullable, so a missing status
  // means the query failed too.
  if (disableIfPeriodLocked && (periodLockStatusError || !lockStatus)) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          "We couldn't check whether this period is locked, so it stays read-only. Please reload the page. If this keeps happening, contact support.",
      },
    };
  }

  // An admin lock is a deliberate, durable decision, so it takes precedence over the transient
  // reasons below — telling someone to wait for a sync would be misleading while the period is
  // closed.
  //
  // A period reads as locked while any month under it is, which is the same test the page needs: a
  // change saved here would touch those months and be rejected, so it is faded rather than left
  // looking editable. Where some months are still open the message names them and points at editing
  // those one at a time.
  if (disableIfPeriodLocked && lockStatus?.isLocked) {
    const monthExceptionsText = getMonthLockExceptionsText(lockStatus);

    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: monthExceptionsText
          ? `${monthExceptionsText} Select a single month to edit the months that are still open.`
          : canSetLock
            ? 'This period is locked. Unlock it on the ASC 350-40 Documents page to make changes.'
            : 'This period has been locked by an admin. Contact your admin to make changes.',
        lockButtons: (
          <Button
            size="xs"
            variant="white"
            rounded="regular"
            as={Link}
            to={getPath({ path: Path.EXPORT_ASC, companyId, fiscalYearId, periodName })}
          >
            Go To Export ASC Documents
          </Button>
        ),
      },
    };
  }

  // Lock if there are ASC cost uploads with pending validation
  const hasAscCostPendingValidation =
    company?.syncedPayrollMonthExpense.some(
      (syncedPayrollMonthExpense) =>
        syncedPayrollMonthExpense.fileUpload && syncedPayrollMonthExpense.fileUpload.isValid === null,
    ) ?? false;
  if (disableIfAscCostPendingValidation && hasAscCostPendingValidation) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          "We're processing your cost file. This usually takes a few minutes. When it's done, the data will load automatically on this page.",
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  if (disableIfOngoingSync && unfinishedSyncEventsPeriod) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: 'Project Analysis is in progress. Please check back later.',
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  if (hasInFlightBulkUpload) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          'Bulk project upload in progress. This usually takes less than 5 minutes. This page will refresh when it completes.',
      },
    };
  }

  return {
    loading: false,
    pageLockStatus: {
      locked: false,
    },
  };
};
